import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { TableFrame } from '../components/table-session/TableFrame';
import { SessionViewport } from '../components/table-session/SessionViewport';
import { HandPanel } from '../components/table-session/HandPanel';
import { AuctionPanel } from '../components/table-session/AuctionPanel';
import { AnswerPanel } from '../components/table-session/AnswerPanel';
import { ExplainPanel, explainVariantFor } from '../components/table-session/ExplainPanel';
import { ResultFeedback } from '../components/table-session/ResultFeedback';
import { isSeat } from '../components/table-session/SeatLabel';
import { StatusLine } from '../components/table-session/StatusLine';
import {
  MASTHEAD_ICON_BUTTON,
  MASTHEAD_FULL_INLINE,
  MASTHEAD_FULL_BLOCK,
  MASTHEAD_PHONE_BLOCK,
  MASTHEAD_PHONE_FLEX,
} from '../components/table-session/mastheadChrome';
import { MastheadInfoRow } from '../components/table-session/MastheadInfoRow';
import { PreviousButton } from '../components/table-session/PreviousButton';
import { PracticeMastheadLinks } from '../components/module-practice/PracticeMastheadLinks';
import { SessionDetailsPopup } from '../components/table-session/SessionDetailsPopup';
import { ExhaustedNotice } from '../components/module-practice/ExhaustedNotice';
import { AskJeffButton } from '../components/ask-jeff/AskJeffButton';
import { BidText } from '../components/common/BidToken';
import { formatBidToken } from '../utils/formatBidToken';
import { useModulePracticeSession } from '../hooks/useModulePracticeSession';
import { useAnswerFlow, callFromAnswerToken } from '../hooks/useAnswerFlow';
import { useOrientationScrollReset } from '../hooks/useOrientationScrollReset';
import { useSessionGuard } from '../contexts/SessionGuardContext';
import { useLegalBids } from '../hooks/useLegalBids';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../hooks/useAuth';
import type { SessionSummaryLocationState } from './SessionSummaryPage';
import {
  FAMILY_SHORT_TITLES,
  findModule,
  findTopic,
  isFamily,
  moduleName,
} from '../config/moduleCatalog';
import type { ModuleFamily, ModuleDefinition, TopicDefinition } from '../config/moduleCatalog';
import type { Position } from '../types/problems';
import { isEditorRole } from '../types/auth';

/**
 * A practice module at the table (feature 023, US5) —
 * `/module-practice/:family/:topicKey/:moduleSlug`.
 *
 * This page is the PRACTICE session's own voice: its pool progress, its streak, its module
 * navigation and its verdict copy. Everything a table session shows whatever its rules — the felt
 * frame, the hand, the auction, the answer pads — comes from `components/table-session/`, and the
 * page supplies only what differs. See that folder's ARCHITECTURE.md before adding a sibling
 * surface here.
 */
export function ModulePracticePage() {
  const { family, topicKey, moduleSlug } = useParams<{
    family: string;
    topicKey: string;
    moduleSlug: string;
  }>();

  if (!isFamily(family)) return <Navigate to="/inventory/precision" replace />;

  const topic = findTopic(topicKey);
  const module = topic && moduleSlug ? findModule(family, topic, moduleSlug) : undefined;

  // An unknown or redirect-only module has no pool to practise — go back to the topic list
  // rather than render an empty table.
  if (!topic || !module || !topic.enabled || module.redirect) {
    return <Navigate to={`/inventory/${family}`} replace />;
  }

  return <ModulePracticeContent family={family} topic={topic} module={module} />;
}

function ModulePracticeContent({
  family,
  topic,
  module,
}: {
  family: ModuleFamily;
  topic: TopicDefinition;
  module: ModuleDefinition;
}) {
  const label = moduleName(module, topic);
  usePageTitle(`${label} — Hawkes Bridge`);

  const session = useModulePracticeSession(family, topic.key, module.slug);
  const flow = useAnswerFlow(session.submit);
  // Phone-only "Problem details" popup (ⓘ in the masthead) — the parity surface for the
  // desktop status line's ID / scoring / difficulty / tournament facts, ported from the
  // pre-023 /problems mobile details popup.
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);

  // The hand id is a tracing aid for the people who curate hands, not a learner-facing fact.
  const { user } = useAuth();
  const canSeeHandId = user?.role === 'Admin' || isEditorRole(user?.role);

  const {
    question,
    result,
    streak,
    score,
    sessionStats,
    loading,
    advancing,
    skipping,
    error,
    exhausted,
    // The step-back trail (client request, 2026-08-03). `isReviewing` is what makes an earlier
    // hand read-only: the pads never come back for it, so nothing is re-graded.
    isReviewing,
    wasSkipped,
    canGoPrevious,
    submittedAnswer,
  } = session;

  // Leaving the table hands the visit's numbers to the summary page — nothing about this
  // session is stored anywhere else, so this is the only moment they can be passed on. A
  // learner who answered nothing has no session to summarise and goes straight back to the
  // topic list, exactly as this link did before the summary existed.
  const hasAnswers = sessionStats.answered > 0;
  const leaveTo = hasAnswers ? '/session-summary' : `/inventory/${family}`;
  const leaveState: SessionSummaryLocationState | undefined = hasAnswers
    ? {
        // The scorecard serves /problems too; this names which card to draw.
        kind: 'module',
        family,
        topicKey: topic.key,
        moduleSlug: module.slug,
        moduleTitle: label,
        topicTitle: topic.title,
        streak,
        score,
        ...sessionStats,
      }
    : undefined;

  // Which calls the auction actually allows. The same hook ProblemsPage and Practice
  // Problems use — the rules live in one place, so the call pad here can never drift from
  // the one on those pages. Must sit above the loading/error returns to keep hook order
  // stable, hence the empty-auction fallback.
  const legalBids = useLegalBids({
    auction: (question?.auction ?? []).map((b) => ({ seat: b.seat as Position, call: b.call })),
    userPosition: isSeat(question?.decisionSeat) ? question.decisionSeat : 'S',
    selectedLevel: flow.selectedLevel,
  });

  // The fixed-height page scroll container (orientation-change reset target).
  const pageScrollRef = useRef<HTMLDivElement>(null);
  useOrientationScrollReset(pageScrollRef);

  // Surface the hand's scoring type beside the banner in the site header (red), as the
  // /problems page does during a session (client request, 2026-07-27). The header reads it
  // from the SessionGuard context; cleared on unmount so other pages don't inherit it.
  const { setScoringType } = useSessionGuard();
  const scoringType = question?.scoringType ?? null;
  useEffect(() => {
    setScoringType(scoringType);
    return () => setScoringType(null);
  }, [scoringType, setScoringType]);

  const handleNext = async () => {
    flow.reset();
    setShowDetailsPopup(false);
    await session.next();
  };

  // Move on without answering. Clears exactly what "Next hand" clears — a skip from halfway
  // down the No path must not leave that path's state behind for the hand that replaces it.
  const handleSkip = async () => {
    flow.reset();
    setShowDetailsPopup(false);
    await session.skip();
  };

  // Back to the hand already dealt with. Clears exactly what "Next hand" clears — a half-entered
  // No path must not follow the learner onto the hand behind — and dismisses the ⓘ popup, whose
  // facts belong to the hand that was on screen when it opened.
  const handlePrevious = () => {
    flow.reset();
    setShowDetailsPopup(false);
    session.previous();
  };

  // The ⓘ details trigger is the LAST item on the masthead's left-hand side (client request,
  // 2026-07-28), trailing the position/id/score line it expands. It is not one of the
  // navigation controls opposite, which take the learner off the hand entirely.
  const mastheadDetailsButton = (
    <button
      type="button"
      onClick={() => setShowDetailsPopup(true)}
      aria-label="Problem details"
      title="Problem details"
      // `narrow:` pairs every md: gate on this page. A landscape phone can be 844px wide —
      // wider than md: — but it is still a phone with a phone's height, and it needs the phone
      // controls (client contract: every control in view). The `narrow` screen is exactly the
      // pre-023 /problems definition: phones by width, OR the supported landscape-phone band.
      className={`${MASTHEAD_ICON_BUTTON} text-cream ${MASTHEAD_PHONE_BLOCK}`}
      data-testid="masthead-details-button"
    >
      <InformationCircleIcon className="h-5 w-5" aria-hidden="true" />
    </button>
  );

  const mastheadLinks = (
    <PracticeMastheadLinks
      backTo={`/inventory/${family}/${topic.key}`}
      leaveTo={leaveTo}
      leaveState={leaveState}
    />
  );

  // The transient/terminal states share the main view's viewport-locked frame (the global
  // footer is suppressed on this route), with the felt stretched to fill it via flex-1 —
  // .problems-scroll is a flex column.
  if (loading) {
    return (
      <SessionViewport>
        <div className="flex-1 bg-felt-bg py-16 text-center text-cream" data-testid="module-loading">
          Dealing…
        </div>
      </SessionViewport>
    );
  }

  // Only a failure with nothing on screen takes the whole view. Once a hand has been dealt, a
  // failed Next / Skip / Submit keeps it on the table with an inline banner instead (spec 030), so
  // the learner loses neither the hand nor the visit's tallies.
  if (error && !question) {
    return (
      <SessionViewport>
        <div className="flex-1 bg-felt-bg py-16 text-center" data-testid="module-error">
          <p className="text-cream">{error}</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => void session.retry()}
              data-testid="module-retry"
              className="min-h-[24px] min-w-[24px] rounded-lg bg-primary-600 px-4 py-2.5 text-[15px] font-bold text-white hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brass"
            >
              Try again
            </button>
            <Link to={`/inventory/${family}/${topic.key}`} className="inline-block text-brass underline">
              Back to modules
            </Link>
          </div>
        </div>
      </SessionViewport>
    );
  }

  if (exhausted || !question) {
    return (
      <SessionViewport>
        <ExhaustedNotice moduleTitle={label} backTo={`/inventory/${family}/${topic.key}`} />
      </SessionViewport>
    );
  }

  const answered = result !== null;

  // Feature 027: every module hand is a downloaded tournament hand, so once an answer is in the
  // commentary row always appears — Jeff's words where the hand has them, otherwise the AI's
  // (which says "Information not available" when the engine did not predict Jeff's call).
  const explainVariant = explainVariantFor({
    answered,
    expertComment: result?.expertComment,
    isTournamentHand: true,
  });
  const isYesNo = question.questionFormat === 'yes-no';

  // The call the hand should have made, in display form. Yes/No drills answer "should you
  // open?", so the CALL lives in `openingBid` — their `correctAnswer` is only "yes"/"no…".
  // Call modules carry no `openingBid` because `correctAnswer` already IS that call.
  // `formatBidToken` turns the raw tournament tokens (p, x, xx) into Pass / Dbl / Rdbl.
  const answerCall = result ? formatBidToken(result.openingBid ?? result.correctAnswer) : null;

  // The learner's own submitted call, echoed on a wrong verdict. Set only when a bid was entered
  // — the call-pad modules, and the "should open something else" bid step on a Yes/No drill. The
  // reason-only Yes/No paths leave it null (their chosen button is still on screen).
  //
  // Read off the recorded token rather than the answer flow, which only ever holds the LIVE
  // question's answer and is reset on every move: a hand stepped back to must echo the call it
  // was actually answered with.
  const submittedCall = callFromAnswerToken(submittedAnswer);
  const userCall = submittedCall ? formatBidToken(submittedCall) : null;

  // Yes/No feedback names Jeff's own action as a self-contained phrase ("Jeff opened 1♥" /
  // "Jeff passed"), so it carries no "Answer:" label. Call modules keep the plain label (FR-017d).
  const jeffPassed = answerCall === 'Pass';
  const feedbackAnswer = isYesNo ? (
    jeffPassed ? (
      'Jeff passed'
    ) : (
      <>
        Jeff opened <BidText text={answerCall ?? ''} />
      </>
    )
  ) : (
    <BidText text={answerCall ?? ''} />
  );

  // Back to the hand behind this one. Shown from the second question of the visit onward, on
  // both the open state and the verdict, so it is reachable whether or not the learner answered.
  //
  // It takes an equal share of its row, and the row's `items-stretch` gives it the height: BOTH of
  // this page's rows pair it with a button carrying no `short:` step ("Skip Hand", "Next hand"),
  // so left to its own size it rendered at two thirds their height on any phone under 750px tall
  // (client report, 2026-08-03). Where the label is hidden — the shortest landscape phones, and
  // under 360px — there is nothing to give width to, so it keeps its own and leaves the room to
  // the action beside it.
  const previousButton = canGoPrevious ? (
    <PreviousButton
      onClick={handlePrevious}
      disabled={advancing || skipping}
      className="flex-1 flat-ls:flex-none xnarrow:flex-none"
    />
  ) : undefined;

  // Feature 025 — Super Editor only; renders nothing for everyone else, so a learner's rows are
  // exactly what they were. It rides in the answer panel's button row, before the answer (beside
  // Skip Hand) and after it (beside Next): the doubt worth sending to Jeff is usually the one felt
  // while deciding. It used to sit in a footer row under the table, in the same column as "Leave
  // the Table" (client decision, 2026-07-22), but on phones that row held nothing else and its
  // height clipped Skip Hand and Next (feature 033, T051; moved at the client's choice,
  // 2026-10-15). The section is the module key, which is exactly how the client names these
  // ("Precision bidding - 1C Opening bid").
  const askJeffButton = (
    <AskJeffButton
      tableResultId={question.sourceTableResultId}
      sectionKey={`${family}.${topic.key}.${module.slug}`}
      // Its own width, on one line; Previous and Skip Hand / Next share the rest.
      className="shrink-0 whitespace-nowrap"
    />
  );

  // One Skip control for every unanswered question, whatever pad is showing (client request,
  // 2026-07-29). Styled as the secondary action to the pad above it — the same brass-on-cream
  // button /problems uses for "Skip Problem", so the two practice surfaces read alike. Previous
  // shares its row rather than taking one of its own: they are the same weight of action, and a
  // second full-width button is height the landscape band does not have.
  const skipButton = (
    <div className="flex items-stretch gap-2">
      {previousButton}
      <button
        type="button"
        onClick={handleSkip}
        disabled={advancing || skipping || flow.reason === 'pass'}
        data-testid="skip-hand"
        // `px-4` matches Previous beside it. Not decoration: `flex-1` bases the split on the
        // CONTENT box, so each item's own horizontal padding is added on top of its half — with
        // padding on one side only, the two halves came out 182px and 158px.
        className="flex-1 rounded-lg border border-brass bg-cream px-4 py-2.5 text-[15px] font-medium text-ink hover:bg-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-brass disabled:cursor-not-allowed disabled:opacity-50"
      >
        {skipping ? 'Skipping…' : 'Skip Hand'}
      </button>
      {askJeffButton}
    </div>
  );

  // The way on, with the way back beside it. Previous leads, because back-then-forward is how
  // every pager on the site reads; Next keeps the width, being the action this panel is for.
  const nextRow = (
    <div className="flex items-stretch gap-2">
      {previousButton}
      <button
        type="button"
        onClick={handleNext}
        disabled={advancing}
        data-testid="next-question"
        // `px-4` for the same reason as Skip Hand above — it equalises the two halves of the row.
        className="flex-1 rounded-lg bg-primary-600 px-4 py-2.5 text-[15px] font-bold text-white hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brass disabled:cursor-not-allowed disabled:opacity-70"
      >
        {advancing ? 'Dealing…' : result?.nextStep ? 'Next call' : 'Next hand'}
      </button>
      {askJeffButton}
    </div>
  );

  // What Practice says once the answer is in: the verdict, the call that was right, and the way
  // on to the next hand (or the next call of a multi-step auction).
  //
  // A hand stepped BACK to shows this same panel even when it was never graded — passed on, or
  // left open before moving forward. Its pads must not return: the hand has been dealt with, and
  // reopening it would offer to grade something the session has already counted.
  const verdict = answered ? (
    <>
      <ResultFeedback
        isCorrect={result.isCorrect}
        answer={feedbackAnswer}
        label={isYesNo ? null : 'Answer:'}
        userCall={userCall ? <BidText text={userCall} /> : undefined}
        // Being right about "no" only rules the suggested opening out; it never says what the
        // hand should have called instead, so that answer is shown either way.
        revealWhenCorrect={result.correctAnswer.trim().toLowerCase().startsWith('no')}
      />
      {nextRow}
    </>
  ) : isReviewing || wasSkipped ? (
    // `wasSkipped` without `isReviewing`: the live hand was passed on but the deal after it failed
    // (spec 030). It stays on screen as dealt-with, and "Next hand" is the way on.
    <>
      <p className="text-[15px] font-bold text-ink-soft" data-testid="module-ungraded">
        {wasSkipped ? 'Skipped' : 'Not answered'}
      </p>
      {nextRow}
    </>
  ) : undefined;

  return (
    <SessionViewport ref={pageScrollRef}>
      {/* A failed Next / Skip / Submit with a hand already dealt (spec 030): the hand, the trail and
          the tallies stay put, and this banner says what went wrong and offers the retry. */}
      {error && (
        <div
          role="alert"
          data-testid="module-inline-error"
          className="flex flex-wrap items-center justify-between gap-2 border-b border-danger bg-cream px-3 py-2 text-[15px] text-ink"
        >
          <span>{error}</span>
          <button
            type="button"
            onClick={() => void session.retry()}
            data-testid="module-inline-retry"
            className="min-h-[24px] min-w-[24px] rounded-md border border-brass bg-cream px-3 py-1 text-[15px] font-medium text-ink hover:bg-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-brass"
          >
            Try again
          </button>
        </div>
      )}
      <TableFrame
        // `flex-1` stretches the felt to the bottom of the viewport-locked container, exactly
        // as the loading/error/exhausted states above already do (.problems-scroll is a flex
        // column). Without it the felt ended at content height and the page's paper background
        // showed through beneath the frame — a pale band under the table on any viewport where
        // the content is shorter than the lock (client report, 2026-07-28). Content taller than
        // the lock still scrolls: a flex item's min-height defaults to auto, so flex-1 can
        // never squash it below its content.
        //
        // Small phones spend the wrapper's breathing room on content instead (no-scroll
        // goal); sm: restores the standard felt margin; padswap (short single-column
        // viewports) gives up nearly all of it — measured at 360x640, where these pixels
        // decide whether the frame fits.
        //
        // `module-table` is index.css's hook for making the frame fill its column (Firefox) — the
        // same fill /problems and the Conceptual Problems page carry. Feature 031: at 956x440
        // Gecko shrink-wrapped this frame and the call column came out 40px wide.
        className="module-table flex-1 bg-felt-bg py-1.5 sm:py-6 padswap:py-0.5"
        subtitle={
        <>
          {/* md+: the full family · topic · module trail. The first segment names the SYSTEM
              being drilled ("Precision system" / "Competitive Bidding"), not the bare word
              "Practice" (client request, 2026-07-29) — the page is reached from a Practice
              button and sits under a Practice heading, so "Practice" restated here said
              nothing, where the family is the one part of the learner's place in the course
              this line was missing. Same source as the phone header trail, so the two agree. */}
          <span className={MASTHEAD_FULL_INLINE}>
            <BidText text={`${FAMILY_SHORT_TITLES[family]} · ${topic.title} · ${label}`} />
          </span>
          {/* Phone: the site header banner already names the module, and repeating it inside
              the frame wastes the masthead (client feedback, 2026-07-27). The h1 keeps an
              sr-only name for screen readers; what SHOWS is the compact info row the pre-023
              /problems phone layout carried: position in pool, streak/score, scoring type. */}
          <span className={`sr-only ${MASTHEAD_PHONE_BLOCK}`}>
            <BidText text={label} />
          </span>
          <MastheadInfoRow
            screens={MASTHEAD_PHONE_FLEX}
            position={question.position}
            poolSize={question.poolSize}
            handId={canSeeHandId ? question.sourceTableResultId : null}
            streak={streak}
            score={score}
            difficulty={question.difficulty}
            // A hand stepped back to has been dealt with, so its level is no longer a reveal to
            // withhold — the same rule /problems applies to a problem it brings back.
            answered={answered || isReviewing}
          />
        </>
      }
        mastheadLinks={mastheadLinks}
        mastheadSubtitleAction={mastheadDetailsButton}
        // The "You hold — <seat>" label rides directly above the hand inside SeatBoardLayout, so
        // it stays over the cards wherever the seat places them — not pinned to the panel top.
        // Suppress the panel-top heading here to avoid showing it twice.
        handLabel={null}
        callLabel={isYesNo ? 'Your decision' : 'Your call'}
        hand={
          <HandPanel
            seat={question.decisionSeat}
            hand={question.hand}
            vulnerability={question.vulnerability}
            dealer={question.dealer}
          />
        }
        auction={
          <AuctionPanel
            auction={question.auction}
            vulnerability={question.vulnerability}
            decisionSeat={question.decisionSeat}
            rewindOn={[question.sourceTableResultId, result]}
          />
        }
        call={
          <AnswerPanel
            isYesNo={isYesNo}
            prompt={<BidText text={question.prompt} />}
            flow={flow}
            legalBids={legalBids}
            busy={advancing}
            footer={skipButton}
            verdict={verdict}
          />
        }
        // Jeff's commentary where the hand has any; otherwise the AI's (feature 027). Every
        // module hand is a downloaded tournament hand, so the AI panel always applies once an
        // answer is in — it says "Information not available" when the engine did not predict
        // Jeff's call, rather than leaving the learner with no row at all. Before the answer
        // there is no panel: commentary is answer-feedback, and `result` is null until then.
        explain={
          explainVariant === 'jeff' ? (
            <ExplainPanel expertComment={result?.expertComment} />
          ) : explainVariant === 'ai' ? (
            <ExplainPanel variant="ai" expertComment={result?.aiComment} />
          ) : undefined
        }
        statusLine={
          // Desktop only: on phones the masthead's compact info row already carries the
          // essentials (position, streak/score, scoring) the way the pre-023 /problems phone
          // layout did — drawing the full line again under the frame would repeat them.
          <div className={MASTHEAD_FULL_BLOCK}>
            <StatusLine
              position={question.position}
              total={question.poolSize}
              streak={streak}
              score={score}
              difficulty={question.difficulty}
              // Module questions are always tournament hands, so the identifier is the hand's
              // tableresult id — the one editors trace a hand by — never a problem serial.
              idLabel="ID"
              idValue={canSeeHandId ? question.sourceTableResultId : undefined}
              scoringType={question.scoringType}
              tournamentTitle={question.tournamentTitle}
              // The review set stores problems, not hands — module sessions omit the pin rather
              // than render a control that cannot work (client decision, 2026-07-20).
              showReviewPin={false}
            />
          </div>
        }
      />

      {showDetailsPopup && (
        <SessionDetailsPopup
          onClose={() => setShowDetailsPopup(false)}
          streak={streak}
          score={score}
          handId={canSeeHandId ? question.sourceTableResultId : null}
          scoringType={question.scoringType}
          difficulty={question.difficulty}
          tournamentTitle={question.tournamentTitle}
        />
      )}
    </SessionViewport>
  );
}
